import type { UseFormRegisterReturn } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { contentFieldAttrs } from "@/lib/blog/locale-options";
import type { BlockFieldControl } from "@/lib/blog/editor-model";

interface BlockFieldProps {
  id: string;
  label: string;
  control: BlockFieldControl;
  locale: string;
  error?: string | undefined;
  registration: UseFormRegisterReturn;
}

/**
 * One editable string in the locale form.
 *
 * `dir` and `lang` go on the control and nowhere else (AM-15.3). Putting them
 * on the surrounding fieldset would mirror the label, the error text and the
 * button row too — and the admin chrome is English. A copy in a right-to-left
 * language is edited in an RTL textarea sitting inside an LTR form, which is
 * what the M5 mockup shows. Every shipped language is LTR today, so `dir` is
 * constant-valued; `lang` still varies and is what a screen reader reads.
 */
export function BlockField({ id, label, control, locale, error, registration }: BlockFieldProps) {
  const attrs = contentFieldAttrs(locale);

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      {control === "textarea" ? (
        <Textarea id={id} dir={attrs.dir} lang={attrs.lang} {...registration} />
      ) : (
        <Input id={id} dir={attrs.dir} lang={attrs.lang} {...registration} />
      )}
      {error !== undefined && (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
